import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  ClockCounterClockwise,
  Eye,
  MagnifyingGlass,
  ShieldCheck,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const SystemAuditPanel = () => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [items, setItems] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    event_types: [],
    users: [],
  });

  const [filters, setFilters] = useState({
    search: '',
    event_type: '',
    user_name: '',
    area: '',
    date_from: '',
    date_to: '',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });

  const [loading, setLoading] = useState(false);

  const fetchAudit = async (requestedPage = 1) => {
    setLoading(true);

    try {
      const params = {
        page: requestedPage,
        limit: pagination.limit,
      };

      Object.entries(filters).forEach(
        ([key, value]) => {
          if (String(value || '').trim()) {
            params[key] = String(value).trim();
          }
        }
      );

      const { data } = await axios.get(
        `${API}/audit/events`,
        {
          params,
          withCredentials: true,
        }
      );

      setItems(data?.items || []);
      setPagination(
        data?.pagination || {
          page: requestedPage,
          limit: 50,
          total: 0,
          pages: 1,
        }
      );

      setFilterOptions(
        data?.filters || {
          event_types: [],
          users: [],
        }
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.detail ||
          'Erro ao carregar a auditoria'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAudit(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      event_type: '',
      user_name: '',
      area: '',
      date_from: '',
      date_to: '',
    });
  };

  return (
    <>
      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
              <ShieldCheck
                size={26}
                weight="duotone"
              />
            </div>

            <div>
              <h3 className="text-lg font-bold text-slate-900">
                Auditoria do Sistema
              </h3>

              <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                Consulte atividades, alterações cadastrais,
                ações da IA, distribuição, nutrição e eventos
                operacionais registrados no ORKESTRY.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800"
          >
            <ClockCounterClockwise size={20} />
            Abrir Auditoria
          </button>
        </div>
      </section>

      {open && (
        <div className="fixed inset-0 z-[100] bg-slate-950/70 p-3 md:p-6 flex items-center justify-center">
          <div className="w-full max-w-7xl h-[94vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Auditoria do Sistema
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  {pagination.total} evento(s) encontrado(s)
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setSelected(null);
                }}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X size={23} />
              </button>
            </header>

            <div className="p-5 border-b border-slate-200 bg-slate-50">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                <div className="xl:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                    Pesquisar
                  </label>

                  <div className="relative">
                    <MagnifyingGlass
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      value={filters.search}
                      onChange={(event) =>
                        updateFilter(
                          'search',
                          event.target.value
                        )
                      }
                      className="input-default pl-10"
                      placeholder="Ação, empreendimento, usuário..."
                    />
                  </div>
                </div>

                <AuditField label="Tipo de evento">
                  <select
                    value={filters.event_type}
                    onChange={(event) =>
                      updateFilter(
                        'event_type',
                        event.target.value
                      )
                    }
                    className="input-default"
                  >
                    <option value="">
                      Todos os tipos
                    </option>

                    {filterOptions.event_types.map(
                      (value) => (
                        <option
                          key={value}
                          value={value}
                        >
                          {formatEventType(value)}
                        </option>
                      )
                    )}
                  </select>
                </AuditField>

                <AuditField label="Usuário">
                  <select
                    value={filters.user_name}
                    onChange={(event) =>
                      updateFilter(
                        'user_name',
                        event.target.value
                      )
                    }
                    className="input-default"
                  >
                    <option value="">
                      Todos os usuários
                    </option>

                    {filterOptions.users.map(
                      (value) => (
                        <option
                          key={value}
                          value={value}
                        >
                          {value}
                        </option>
                      )
                    )}
                  </select>
                </AuditField>

                <AuditField label="Área">
                  <input
                    value={filters.area}
                    onChange={(event) =>
                      updateFilter(
                        'area',
                        event.target.value
                      )
                    }
                    className="input-default"
                    placeholder="Ex.: enterprise, sales_dna"
                  />
                </AuditField>

                <AuditField label="De">
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(event) =>
                      updateFilter(
                        'date_from',
                        event.target.value
                      )
                    }
                    className="input-default"
                  />
                </AuditField>

                <AuditField label="Até">
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(event) =>
                      updateFilter(
                        'date_to',
                        event.target.value
                      )
                    }
                    className="input-default"
                  />
                </AuditField>

                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    onClick={() => fetchAudit(1)}
                    disabled={loading}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-600 text-white font-semibold disabled:opacity-50"
                  >
                    <MagnifyingGlass size={18} />
                    Filtrar
                  </button>

                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-4 py-3 rounded-xl border border-slate-300 text-slate-700 font-semibold"
                  >
                    Limpar
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-auto">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <ArrowClockwise
                    size={34}
                    className="animate-spin text-indigo-600"
                  />
                </div>
              ) : items.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-500">
                  Nenhum evento encontrado.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {items.map((event, index) => (
                    <article
                      key={
                        event.id ||
                        `${event.timestamp}-${index}`
                      }
                      className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 hover:bg-slate-50"
                    >
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0">
                          <ClockCounterClockwise
                            size={20}
                          />
                        </div>

                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-bold text-slate-900">
                              {event.title ||
                                formatEventType(
                                  event.type
                                )}
                            </p>

                            {event.type && (
                              <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold">
                                {formatEventType(
                                  event.type
                                )}
                              </span>
                            )}
                          </div>

                          {event.description && (
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                              {event.description}
                            </p>
                          )}

                          <p className="text-xs text-slate-500 mt-2">
                            {event.user_name || 'Sistema'}
                            {' • '}
                            {formatDateTime(
                              event.timestamp ||
                                event.created_at
                            )}

                            {(event.entity_name ||
                              event.enterprise_name) && (
                              <>
                                {' • '}
                                {event.entity_name ||
                                  event.enterprise_name}
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setSelected(event)
                        }
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold shrink-0"
                      >
                        <Eye size={17} />
                        Ver detalhes
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </div>

            <footer className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-4">
              <p className="text-sm text-slate-500">
                Página {pagination.page} de{' '}
                {pagination.pages}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={
                    loading ||
                    pagination.page <= 1
                  }
                  onClick={() =>
                    fetchAudit(
                      pagination.page - 1
                    )
                  }
                  className="p-2 rounded-lg border border-slate-300 disabled:opacity-40"
                >
                  <CaretLeft size={20} />
                </button>

                <button
                  type="button"
                  disabled={
                    loading ||
                    pagination.page >=
                      pagination.pages
                  }
                  onClick={() =>
                    fetchAudit(
                      pagination.page + 1
                    )
                  }
                  className="p-2 rounded-lg border border-slate-300 disabled:opacity-40"
                >
                  <CaretRight size={20} />
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {selected && (
        <AuditDetails
          event={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};

const AuditDetails = ({
  event,
  onClose,
}) => (
  <div className="fixed inset-0 z-[110] bg-slate-950/60 p-4 flex items-center justify-center">
    <div className="w-full max-w-2xl max-h-[88vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
      <header className="p-5 border-b border-slate-200 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">
            Detalhes da atividade
          </h3>

          <p className="text-sm text-slate-500 mt-1">
            {formatDateTime(
              event.timestamp ||
                event.created_at
            )}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-slate-100"
        >
          <X size={22} />
        </button>
      </header>

      <div className="p-5 overflow-auto space-y-5">
        <Detail
          label="Ação"
          value={
            event.title ||
            formatEventType(event.type)
          }
        />

        <Detail
          label="Descrição"
          value={event.description}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Detail
            label="Usuário"
            value={event.user_name || 'Sistema'}
          />

          <Detail
            label="Tipo"
            value={formatEventType(event.type)}
          />

          <Detail
            label="Área"
            value={
              event.section ||
              event.entity_type ||
              inferArea(event)
            }
          />

          <Detail
            label="Registro"
            value={
              event.entity_name ||
              event.enterprise_name ||
              event.lead_id ||
              event.entity_id
            }
          />
        </div>

        {event.before != null && (
          <JsonBlock
            title="Antes"
            value={event.before}
          />
        )}

        {event.after != null && (
          <JsonBlock
            title="Depois"
            value={event.after}
          />
        )}

        {event.metadata &&
          Object.keys(event.metadata).length > 0 && (
            <JsonBlock
              title="Metadados"
              value={event.metadata}
            />
          )}
      </div>
    </div>
  </div>
);

const AuditField = ({
  label,
  children,
}) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
      {label}
    </label>

    {children}
  </div>
);

const Detail = ({
  label,
  value,
}) => {
  if (
    value === undefined ||
    value === null ||
    value === ''
  ) {
    return null;
  }

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>

      <p className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">
        {String(value)}
      </p>
    </div>
  );
};

const JsonBlock = ({
  title,
  value,
}) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
      {title}
    </p>

    <pre className="rounded-xl bg-slate-950 text-slate-100 p-4 text-xs overflow-auto whitespace-pre-wrap">
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);

const inferArea = (event) => {
  if (event.enterprise_id) {
    return 'Empreendimentos';
  }

  if (event.lead_id) {
    return 'Leads e atendimento';
  }

  if (
    String(event.type || '').includes(
      'sales_dna'
    )
  ) {
    return 'Sales DNA';
  }

  return 'Sistema';
};

const formatEventType = (value) => {
  if (!value) return 'Atividade';

  return String(value)
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
};

const formatDateTime = (value) => {
  if (!value) return 'Data não informada';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('pt-BR');
};

export default SystemAuditPanel;
